import { PngExportResult, PngExportSeries } from 'utils/chartPngExport';
import {
  EXPORT_CHART_SPECS,
  ExportChartSpec,
  ExportChartView,
  getExportChartLegend,
  getExportDataDisplay,
} from './chartSpecs';
import { ExportChartContext, ExportChartItem, fetchExportChart } from './chartData';

export interface ExportChartCaptureResult {
  view: ExportChartView;
  blob: Blob;
}

export interface CaptureProgress {
  current: number;
  total: number;
  view: ExportChartView;
}

export interface CaptureOneInput {
  spec: ExportChartSpec;
  chartItems: ExportChartItem[];
  dataDisplay: { key: string; checked: boolean }[];
  series: PngExportSeries[];
}

export interface RunChartCaptureOptions {
  ctx: ExportChartContext;
  hasSkillDive: boolean;
  /** Render the chart off-viewport and capture it as a PNG. */
  captureOne: (input: CaptureOneInput) => Promise<PngExportResult>;
  specs?: ExportChartSpec[];
  fetchChart?: (view: ExportChartView, ctx: ExportChartContext) => Promise<ExportChartItem[]>;
  onProgress?: (progress: CaptureProgress) => void;
}

/**
 * Sequentially fetch and capture every page-export chart. Each view is fetched
 * in isolation, rendered with all series enabled, and captured with its full
 * legend. All-or-nothing: any failed capture fails the whole run.
 */
export async function runChartCapture({
  ctx,
  hasSkillDive,
  captureOne,
  specs = EXPORT_CHART_SPECS,
  fetchChart = fetchExportChart,
  onProgress,
}: RunChartCaptureOptions): Promise<ExportChartCaptureResult[]> {
  const results: ExportChartCaptureResult[] = [];
  for (let i = 0; i < specs.length; i++) {
    const spec = specs[i];
    onProgress?.({ current: i, total: specs.length, view: spec.view });
    const chartItems = await fetchChart(spec.view, ctx);
    const result = await captureOne({
      spec,
      chartItems,
      dataDisplay: getExportDataDisplay(spec, hasSkillDive),
      series: getExportChartLegend(spec, hasSkillDive),
    });
    if (result.ok === false) {
      throw new Error(`export_chart_capture_failed:${spec.view}:${result.reason}`);
    }
    results.push({ view: spec.view, blob: result.blob });
  }
  return results;
}
